/**
 * Resolve how much of an `output_text.done` / snapshot payload is NEW relative
 * to text already streamed via deltas.
 *
 * After tool rounds, `accumulated` holds prior legs while `doneText` is often
 * only the latest message — or a full cumulative snapshot that was already
 * streamed. Blindly appending re-prints the answer (the "same rundown after
 * every tool card" bug).
 */

/** Ignore tiny accidental overlaps (letters, newlines) in suffix matching. */
const MIN_SUFFIX_OVERLAP = 32

export function resolveOutputTextDoneDelta(
  accumulated: string,
  doneText: string
): string {
  if (!doneText) return ''
  if (!accumulated) return doneText
  if (accumulated === doneText || accumulated.endsWith(doneText)) return ''
  if (doneText.startsWith(accumulated)) {
    return doneText.slice(accumulated.length)
  }
  if (accumulated.includes(doneText)) return ''

  // Anchor: if the start of doneText already appears in accumulated, compare
  // from that point and only emit a true novel tail.
  const probeLen = Math.min(80, doneText.length)
  const probe = doneText.slice(0, probeLen)
  let anchor = accumulated.indexOf(probe)
  while (anchor >= 0) {
    const fromAnchor = accumulated.slice(anchor)
    if (fromAnchor.startsWith(doneText)) {
      return ''
    }
    if (doneText.startsWith(fromAnchor) && fromAnchor.length >= MIN_SUFFIX_OVERLAP) {
      return doneText.slice(fromAnchor.length)
    }
    anchor = accumulated.indexOf(probe, anchor + 1)
  }

  // Meaningful suffix overlap only — never match 1–31 char accidents that
  // would re-append nearly the entire snapshot.
  const max = Math.min(accumulated.length, doneText.length)
  const minOverlap = Math.min(MIN_SUFFIX_OVERLAP, doneText.length)
  for (let len = max; len >= minOverlap; len -= 1) {
    if (doneText.startsWith(accumulated.slice(-len))) {
      return doneText.slice(len)
    }
  }

  // No meaningful overlap — fresh post-tool leg.
  return doneText
}

/**
 * Client-side mirror: when appending a stream `0:` text frame, drop frames that
 * are already present as a trailing/full snapshot so a buggy server dump cannot
 * permanently bake repetition into the persisted message row.
 */
export function appendStreamTextDelta(accumulated: string, delta: string): string {
  if (!delta) return accumulated
  if (!accumulated) return delta

  // Fast path for normal token-sized deltas.
  if (delta.length < MIN_SUFFIX_OVERLAP) {
    if (accumulated.endsWith(delta)) return accumulated
    return accumulated + delta
  }

  const novel = resolveOutputTextDoneDelta(accumulated, delta)
  if (!novel) return accumulated
  return accumulated + novel
}
